/**
 * PWA candidato — helpers de instalação (Fase 1).
 * Inativo quando REACT_APP_ENABLE_PWA !== 'true'.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getTenantCandidatePwaContracted,
  subscribeTenantCandidatePwaContracted,
} from './tenantPwaContract';

export const CANDIDATE_PWA_ENABLED = process.env.REACT_APP_ENABLE_PWA === 'true';

/** PWA ativo para este tenant (build + contrato no master). */
export function isCandidatePwaFeatureActive() {
  return CANDIDATE_PWA_ENABLED && getTenantCandidatePwaContracted();
}

const DISMISS_KEY = 'ingressa_pwa_install_dismissed';

export function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );
}

export function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/** Celular/tablet — banner de instalação só faz sentido nesses clientes. */
export function isMobileClientForInstallBanner() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua)) {
    return true;
  }
  // iPadOS recente pode se identificar como Macintosh + touch
  if (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) {
    return true;
  }
  return false;
}

export const STAFF_ROLES = ['admin', 'analyst', 'master', 'campus'];

export function isStaffRole(role) {
  return STAFF_ROLES.includes(role || '');
}

/** App instalado na tela inicial (PWA candidato). */
export function isCandidatePwaContext() {
  return isCandidatePwaFeatureActive() && isStandalonePwa();
}

export function blocksStaffInCandidatePwa(role) {
  return isCandidatePwaContext() && isStaffRole(role);
}

export const CANDIDATE_PWA_STAFF_BLOCK_MESSAGE =
  'Este aplicativo é apenas para candidatos.';

/** Atualiza título e ícone Apple/PWA conforme sigla da instituição. */
export function syncCandidatePwaBranding(config) {
  if (!isCandidatePwaFeatureActive() || typeof document === 'undefined') return;
  const sigla = (config?.institution_name || '').trim() || 'Ingressa';
  const icon = '/pwa/icon-192.png';
  let metaTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!metaTitle) {
    metaTitle = document.createElement('meta');
    metaTitle.name = 'apple-mobile-web-app-title';
    document.head.appendChild(metaTitle);
  }
  metaTitle.content = sigla;
  let touchIcon = document.querySelector('link[rel="apple-touch-icon"]');
  if (!touchIcon) {
    touchIcon = document.createElement('link');
    touchIcon.rel = 'apple-touch-icon';
    document.head.appendChild(touchIcon);
  }
  touchIcon.href = icon;
}

export function useCandidatePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [tenantContracted, setTenantContracted] = useState(getTenantCandidatePwaContracted());
  const [dismissed, setDismissed] = useState(
    () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1',
  );
  const standalone = isStandalonePwa();
  const pwaActive = CANDIDATE_PWA_ENABLED && tenantContracted;

  useEffect(() => subscribeTenantCandidatePwaContracted(setTenantContracted), []);

  useEffect(() => {
    if (!pwaActive || standalone) return undefined;
    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, [standalone, pwaActive]);

  const install = useCallback(async () => {
    if (!deferredPrompt) return { ok: false, reason: 'unavailable' };
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return { ok: outcome === 'accepted', reason: outcome };
    } catch {
      return { ok: false, reason: 'error' };
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }, []);

  const showBanner =
    pwaActive && !standalone && !dismissed && isMobileClientForInstallBanner();

  return {
    pwaEnabled: pwaActive,
    standalone,
    dismissed,
    showBanner,
    install,
    dismiss,
  };
}
